import { useState } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, ShieldCheck, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { HE } from "@/lib/i18n";
import { t } from "@/lib/strings";

const BASE_CODES = [
  { code: "+1", label: "🇺🇸 +1" },
  { code: "+44", label: "🇬🇧 +44" },
  { code: "+91", label: "🇮🇳 +91" },
  { code: "+61", label: "🇦🇺 +61" },
  { code: "+49", label: "🇩🇪 +49" },
  { code: "+971", label: "🇦🇪 +971" },
  { code: "+65", label: "🇸🇬 +65" },
  { code: "+972", label: "🇮🇱 +972" },
];

const COUNTRY_CODES = HE
  ? [{ code: "+972", label: "🇮🇱 +972" }, ...BASE_CODES.filter((c) => c.code !== "+972")]
  : BASE_CODES;

export const LeadCaptureForm = ({
  analysisId,
  domain,
  campaign,
  onUnlocked,
  variant = "dark",
  ctaText,
}) => {
  const [countryCode, setCountryCode] = useState(HE ? "+972" : "+1");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const light = variant === "light";
  const cta = ctaText || t("lead.cta");

  const submit = async (e) => {
    e.preventDefault();
    let digits = phone.replace(/\D/g, "");
    if (countryCode === "+972") digits = digits.replace(/^0/, "");
    if (digits.length < 6) {
      toast.error(t("lead.errorPhone"));
      return;
    }
    setLoading(true);
    try {
      const data = await api.createLead({
        analysis_id: analysisId,
        phone_number: digits,
        country_code: countryCode,
        source_campaign: campaign || "organic",
      });
      toast.success(t("lead.success"));
      onUnlocked(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("lead.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="w-full" data-testid="lead-capture-form">
      <label className={`font-data text-[11px] uppercase tracking-[0.22em] ${light ? "text-neutral-500" : "text-neutral-400"}`}>
        {t("lead.label")}
      </label>
      <div className={`mt-2 flex items-stretch bg-white border-2 transition-colors focus-within:border-conversion ${light ? "border-obsidian" : "border-white"}`}>
        <div className={`flex items-center pl-3 ${HE ? "border-l-2" : "border-r-2"} border-neutral-200`}>
          <MessageCircle className="h-5 w-5 text-[#25D366]" />
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="bg-transparent font-data text-obsidian text-sm py-4 pl-2 pr-1 focus:outline-none cursor-pointer"
            data-testid="country-code-select"
            dir="ltr"
          >
            {COUNTRY_CODES.map((c) => (
              <option key={c.code} value={c.code}>{c.label}</option>
            ))}
          </select>
        </div>
        <input
          type="tel"
          inputMode="numeric"
          dir="ltr"
          placeholder={t("lead.placeholder")}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={`flex-1 min-w-0 bg-transparent font-data text-obsidian text-base px-3 focus:outline-none placeholder:text-neutral-400 ${HE ? "text-left" : ""}`}
          data-testid="phone-input"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="mt-3 w-full bg-[#25D366] hover:bg-[#1eb257] text-obsidian font-bold uppercase tracking-wide px-6 py-4 flex items-center justify-center gap-2 transition-all duration-200 hover:-translate-y-[2px] hover:shadow-[5px_5px_0px_0px_rgba(255,69,0,1)] active:translate-y-0 active:shadow-none disabled:opacity-60"
        data-testid="whatsapp-submit"
      >
        {loading ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <>{cta} <ArrowRight className={`h-5 w-5 ${HE ? "rotate-180" : ""}`} /></>
        )}
      </button>

      <div className={`flex items-center justify-center gap-1.5 mt-3 text-xs ${light ? "text-neutral-600" : "text-neutral-500"}`}>
        <ShieldCheck className="h-3.5 w-3.5 text-[#25D366]" />
        {HE ? (
          <span>בלחיצה תקבלו את הדוח + עד 3 הודעות המשך עם התיקונים שלכם והצעה אחת. <span className="font-bold">השיבו "הסר" בכל שלב.</span></span>
        ) : (
          <span>Your report + 3 short follow-ups with your fixes and one offer. <span className="font-bold">Reply STOP anytime.</span></span>
        )}
      </div>
    </form>
  );
};

export default LeadCaptureForm;
